import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_ENDPOINTS } from '../../config/api';
import { logoutUser } from './authSlice';

const buildQueryString = (params) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    query.append(key, value);
  });
  const queryString = query.toString();
  return queryString ? `?${queryString}` : '';
};

const fetchWithAuth = async (url, getState, signal) => {
  const token = getState()?.auth?.token;
  if (!token) {
    throw new Error('認証が必要です');
  }

  const response = await fetch(url, {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  const result = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(result?.message || 'データの取得に失敗しました');
  }

  return result;
};

export const fetchHealthtronUserStats = createAsyncThunk(
  'healthtronUser/fetchStats',
  async ({ from, to, ageMin, ageMax } = {}, { rejectWithValue, getState, signal }) => {
    try {
      const url = `${API_ENDPOINTS.ADMIN_HEALTHTRON_USER_STATS}${buildQueryString({
        from,
        to,
        age_min: ageMin,
        age_max: ageMax,
      })}`;
      const result = await fetchWithAuth(url, getState, signal);
      return result?.data ?? null;
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

export const fetchHealthtronUsers = createAsyncThunk(
  'healthtronUser/fetchUsers',
  async (
    { page = 1, perPage = 10, search, from, to, ageMin, ageMax, sort } = {},
    { rejectWithValue, getState, signal }
  ) => {
    try {
      const url = `${API_ENDPOINTS.ADMIN_HEALTHTRON_USERS}${buildQueryString({
        page,
        per_page: perPage,
        search,
        from,
        to,
        age_min: ageMin,
        age_max: ageMax,
        sort,
      })}`;
      const result = await fetchWithAuth(url, getState, signal);
      return {
        users: Array.isArray(result?.data) ? result.data : [],
        pagination: result?.pagination ?? initialPagination,
      };
    } catch (error) {
      return rejectWithValue(error.message || 'ネットワークエラーが発生しました');
    }
  }
);

const initialPagination = {
  currentPage: 1,
  lastPage: 1,
  perPage: 10,
  total: 0,
};

const initialState = {
  stats: null,
  users: [],
  pagination: initialPagination,
  isStatsLoading: false,
  isUsersLoading: false,
  statsError: null,
  usersError: null,
  latestStatsRequestId: null,
  latestUsersRequestId: null,
};

const isLatestStatsRequest = (state, action) =>
  action.meta.requestId === state.latestStatsRequestId;

const isLatestUsersRequest = (state, action) =>
  action.meta.requestId === state.latestUsersRequestId;

const healthtronUserSlice = createSlice({
  name: 'healthtronUser',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchHealthtronUserStats.pending, (state, action) => {
        state.latestStatsRequestId = action.meta.requestId;
        state.isStatsLoading = true;
        state.statsError = null;
      })
      .addCase(fetchHealthtronUserStats.fulfilled, (state, action) => {
        if (!isLatestStatsRequest(state, action)) return;
        state.isStatsLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchHealthtronUserStats.rejected, (state, action) => {
        if (!isLatestStatsRequest(state, action) || action.meta.aborted) return;
        state.isStatsLoading = false;
        state.stats = null;
        state.statsError = action.payload || action.error?.message || 'データの取得に失敗しました';
      })
      .addCase(fetchHealthtronUsers.pending, (state, action) => {
        state.latestUsersRequestId = action.meta.requestId;
        state.isUsersLoading = true;
        state.usersError = null;
      })
      .addCase(fetchHealthtronUsers.fulfilled, (state, action) => {
        if (!isLatestUsersRequest(state, action)) return;
        state.isUsersLoading = false;
        state.users = action.payload.users;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchHealthtronUsers.rejected, (state, action) => {
        if (!isLatestUsersRequest(state, action) || action.meta.aborted) return;
        state.isUsersLoading = false;
        state.users = [];
        state.pagination = initialPagination;
        state.usersError = action.payload || action.error?.message || 'データの取得に失敗しました';
      })
      .addCase(logoutUser.fulfilled, () => ({ ...initialState }));
  },
});

export default healthtronUserSlice.reducer;
